import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { FiArrowRight, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import API from '../../api/axios';
import { getImageUrl } from '../../utils/helpers';

const defaultBanners = [
  {
    _id: 'd1',
    title: "Pakistan's Leading Medical Equipment Supplier",
    subtitle: 'Quality hospital furniture, electromedical devices, and diagnostic equipment delivered nationwide.',
    buttonText: 'Shop Now',
    link: '/shop',
    backgroundColor: '#1e3a5f',
    textColor: '#ffffff',
    badge: 'Trusted by 500+ Hospitals',
  },
  {
    _id: 'd2',
    title: 'Hospital Furniture & Surgical Equipment',
    subtitle: 'Premium beds, trolleys, surgical tables and ICU equipment for modern healthcare facilities.',
    buttonText: 'Explore Products',
    link: '/shop',
    backgroundColor: '#0f2a45',
    textColor: '#ffffff',
    badge: 'ISO Certified Products',
  },
  {
    _id: 'd3',
    title: 'Electromedical & Diagnostic Devices',
    subtitle: 'State-of-the-art patient monitors, ECG machines, and diagnostic equipment at competitive prices.',
    buttonText: 'View Catalog',
    link: '/shop',
    backgroundColor: '#1a3d5c',
    textColor: '#ffffff',
    badge: 'Free Nationwide Delivery',
  },
];

const PrevArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 border border-white/20"
  >
    <FiChevronLeft size={20} />
  </button>
);

const NextArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 border border-white/20"
  >
    <FiChevronRight size={20} />
  </button>
);

const stats = [
  { label: 'Products', value: '2,000+' },
  { label: 'Hospitals Served', value: '500+' },
  { label: 'Years Experience', value: '15+' },
  { label: 'Cities Covered', value: '50+' },
];

const HeroBanner = () => {
  const [banners, setBanners] = useState(defaultBanners);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    API.get('/banners?position=hero')
      .then(({ data }) => { if (data.length > 0) setBanners(data); })
      .catch(() => {});
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 700,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5500,
    arrows: true,
    pauseOnHover: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    afterChange: (i) => setCurrentSlide(i),
    appendDots: (dots) => (
      <div style={{ bottom: '16px' }}>
        <ul className="flex justify-center gap-2">{dots}</ul>
      </div>
    ),
    customPaging: (i) => (
      <div className={`w-2 h-2 rounded-full transition-all duration-300 ${i === currentSlide ? 'bg-white w-6' : 'bg-white/40'}`} />
    ),
  };

  return (
    <div className="w-full overflow-hidden">
      <div className="relative">
        <Slider {...sliderSettings}>
          {banners.map((banner) => (
            <div key={banner._id}>
              <div
                className="relative w-full flex items-center overflow-hidden"
                style={{
                  background: banner.image
                    ? `url(${getImageUrl(banner.image)}) center/cover no-repeat`
                    : `linear-gradient(135deg, ${banner.backgroundColor || '#1e3a5f'} 0%, ${banner.backgroundColor ? banner.backgroundColor + 'dd' : '#0f2a45'} 100%)`,
                  minHeight: '480px',
                }}
              >
                {/* Dark overlay for image banners */}
                {banner.image && (
                  <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
                )}

                {/* Decorative circles */}
                <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, white 0%, transparent 70%)' }} />
                <div className="absolute -right-8 bottom-0 w-56 h-56 rounded-full opacity-[0.06]" style={{ background: 'radial-gradient(circle, white 0%, transparent 70%)' }} />
                <div className="absolute left-1/2 top-0 w-96 h-96 rounded-full opacity-[0.04]" style={{ background: 'radial-gradient(circle, white 0%, transparent 70%)' }} />

                {/* Diagonal accent line */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute -right-12 top-0 bottom-0 w-64 bg-white/5 skew-x-12" />
                  <div className="absolute -right-24 top-0 bottom-0 w-32 bg-white/5 skew-x-12" />
                </div>

                <div className="relative container-custom py-16 z-10">
                  <div className="max-w-xl">
                    {banner.badge && (
                      <span className="inline-block bg-accent-500 text-white text-xs font-bold px-3 py-1.5 rounded-full mb-5 tracking-wide uppercase shadow-lg">
                        {banner.badge}
                      </span>
                    )}

                    <h1
                      className="text-3xl md:text-5xl font-extrabold mb-5 leading-tight tracking-tight"
                      style={{ color: banner.textColor || '#fff' }}
                    >
                      {banner.title}
                    </h1>

                    {banner.subtitle && (
                      <p
                        className="text-base md:text-lg mb-8 leading-relaxed"
                        style={{ color: banner.textColor ? banner.textColor + 'cc' : 'rgba(255,255,255,0.8)' }}
                      >
                        {banner.subtitle}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-3">
                      <Link
                        to={banner.link || '/shop'}
                        className="inline-flex items-center gap-2 bg-white text-primary-700 font-bold px-7 py-3.5 rounded-xl hover:bg-gray-50 active:scale-95 transition-all text-base shadow-xl"
                      >
                        {banner.buttonText || 'Shop Now'}
                        <FiArrowRight size={16} />
                      </Link>
                      <Link
                        to="/shop?featured=true"
                        className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold px-7 py-3.5 rounded-xl transition-all text-base backdrop-blur-sm"
                      >
                        Featured Products
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>

      {/* Stats bar */}
      <div className="bg-primary-700 text-white">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s) => (
              <div key={s.label} className="py-4 px-6 text-center">
                <p className="text-xl md:text-2xl font-extrabold text-accent-400">{s.value}</p>
                <p className="text-xs text-primary-200 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;
